/**
 * Gate Pass register — one screen, four views.
 *
 * A store keeper thinks "gate pass register", not "outward list" and "inward list"
 * as separate products, so this is one nav item with tabs rather than four.
 *
 * The stat cards are the point of the screen: Overdue is the number a hospital
 * actually loses money to, so it is on the surface in alert colour, not behind a
 * filter dropdown. All four numbers come from the three queue calls the hub makes
 * once on mount — no card fetches its own count.
 */
import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvButton from '../../components/InvButton';
import InvTabBar from '../../components/InvTabBar';
import StatCard from '../../components/StatCard';
import GatePassListPanel from '../../components/stock/GatePassListPanel';
import GatePassOutstandingPanel from '../../components/stock/GatePassOutstandingPanel';
import useGatePassQueues from '../../hooks/useGatePassQueues';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { INV_ICONS } from '../../constants/icons';

export default function GatePassHub() {
  const history = useHistory();
  const { url } = useRouteMatch();
  const { can, myStoreId, myStoreIds } = useInvRole();

  // Routes use the singular segment; the list lives on the plural one.
  const base = url.replace(/\/gate-passes\/?$/, '/gate-pass');
  const stockBase = url.replace(/\/gate-passes\/?$/, '');

  // A store-scoped user only ever sees their own store, so scope the queues too —
  // otherwise their "needs approval" count includes other people's work.
  const scopedStoreId = myStoreIds.length === 1 ? myStoreId : '';
  const queues = useGatePassQueues(scopedStoreId);

  const [tab, setTab] = useState('outward');
  const [overdueOnly, setOverdueOnly] = useState(false);

  function showReturns(onlyOverdue) {
    setOverdueOnly(onlyOverdue);
    setTab('returns');
  }

  const tabs = [
    { id: 'outward', label: 'Outward' },
    { id: 'inward', label: 'Inward' },
    {
      id: 'returns',
      label: queues.outstanding.length ? `Pending Returns (${queues.outstanding.length})` : 'Pending Returns',
    },
    {
      id: 'approvals',
      label: (() => {
        const waiting = queues.approvalQueue.length + queues.gateQueue.length;
        return waiting ? `Approvals (${waiting})` : 'Approvals';
      })(),
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumb={['Inventory Management', 'Stock']}
        title="Gate Pass"
        description="Material entering or leaving hospital premises"
        actions={(
          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* <InvButton variant="outlined" size="small" onClick={() => history.push(stockBase)}>
              Back to Stock
            </InvButton> */}
            {can(INV_ACTIONS.GATEPASS_CREATE) && (
              <InvButton
                size="small"
                startIcon={<AddIcon />}
                onClick={() => history.push(`${base}/new`)}
              >
                New Gate Pass
              </InvButton>
            )}
          </Box>
        )}
      />

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Needs approval"
            value={queues.approvalQueue.length}
            icon={INV_ICONS.storeQueues}
            tone="amber"
            loading={queues.loading}
            selected={tab === 'approvals'}
            onClick={() => setTab('approvals')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Ready to leave"
            value={queues.gateQueue.length}
            icon={INV_ICONS.shipping}
            tone="blue"
            loading={queues.loading}
            selected={tab === 'approvals'}
            onClick={() => setTab('approvals')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Out & not returned"
            value={queues.outstanding.length}
            icon={INV_ICONS.returns}
            loading={queues.loading}
            selected={tab === 'returns' && !overdueOnly}
            onClick={() => showReturns(false)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Overdue"
            value={queues.overdue.length}
            icon={INV_ICONS.expiry}
            tone="red"
            loading={queues.loading}
            selected={tab === 'returns' && overdueOnly}
            onClick={() => showReturns(true)}
          />
        </Grid>
      </Grid>

      <InvTabBar
        tabs={tabs}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="Gate pass register views"
      />

      {tab === 'returns' ? (
        <GatePassOutstandingPanel
          base={base}
          queues={queues}
          overdueOnly={overdueOnly}
          onOverdueOnlyChange={setOverdueOnly}
        />
      ) : (
        <GatePassListPanel key={tab} tab={tab} base={base} queues={queues} />
      )}
    </Box>
  );
}
